import { useState } from 'react';
import { FileText, Target, AlertTriangle, ExternalLink, Edit2, Trash2, Archive, ArchiveRestore, CheckCircle, CheckCircle2, Circle, List, Map, Folder, Plus, ChevronRight } from 'lucide-react';
import Section from '../components/Section';
import ConfirmModal from '../components/ConfirmModal';
import DocumentModal from '../components/DocumentModal';
import GoalModal from '../components/GoalModal';
import ProjectModal from '../components/ProjectModal';
import TaskModal from '../components/TaskModal';
import { GoalDetailPanel } from '../components/goals';
import { useDocuments } from '../hooks/useDocuments';
import { useGoals } from '../hooks/useGoals';
import { useProjects } from '../hooks/useProjects';
import { useTasks, TASK_STATUSES } from '../hooks/useTasks';
import { useTimeBlocks } from '../hooks/useTimeBlocks';
import { useAuth } from '../hooks/useAuth';
import FeatureOnboarding from '../components/common/FeatureOnboarding';

function formatDate(dateStr) {
  if (!dateStr) return '';
  const date = new Date(dateStr + 'T00:00:00');
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatWeekOf(dateStr) {
  if (!dateStr) return '';
  const date = new Date(dateStr + 'T00:00:00');
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

// Timeline view helpers
const getQuarter = (dateStr) => {
  if (!dateStr) return null;
  const month = new Date(dateStr + 'T00:00:00').getMonth();
  if (month <= 2) return 'Q1';
  if (month <= 5) return 'Q2';
  if (month <= 8) return 'Q3';
  return 'Q4';
};

const getMonthIndex = (dateStr) => {
  if (!dateStr) return 0;
  return new Date(dateStr + 'T00:00:00').getMonth();
};

const isGoalOverdue = (dueDate) => {
  if (!dueDate) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(dueDate + 'T00:00:00');
  return due < today;
};

const getCompletedAtDate = (completedAt) => {
  if (!completedAt) return null;
  if (completedAt.toDate) {
    return completedAt.toDate();
  }
  return new Date(completedAt);
};

const quarterColors = {
  Q1: { line: 'bg-blue-500', bg: 'bg-blue-500/10', border: 'border-blue-500/30', text: 'text-blue-400', dot: 'bg-blue-500' },
  Q2: { line: 'bg-emerald-500', bg: 'bg-emerald-500/10', border: 'border-emerald-500/30', text: 'text-emerald-400', dot: 'bg-emerald-500' },
  Q3: { line: 'bg-amber-500', bg: 'bg-amber-500/10', border: 'border-amber-500/30', text: 'text-amber-400', dot: 'bg-amber-500' },
  Q4: { line: 'bg-purple-500', bg: 'bg-purple-500/10', border: 'border-purple-500/30', text: 'text-purple-400', dot: 'bg-purple-500' },
};

// Timeline View Component
function TimelineView({ goals, getGoalOwnerName }) {
  const currentMonth = new Date().getMonth();
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
                      'July', 'August', 'September', 'October', 'November', 'December'];

  const completedGoals = goals.filter(g => g.completed);
  const overdueGoals = goals.filter(g => !g.completed && isGoalOverdue(g.dueDate));
  const activeGoals = goals.filter(g => !g.completed && !isGoalOverdue(g.dueDate));

  const goalsByMonth = {};

  activeGoals.forEach(goal => {
    const monthIdx = getMonthIndex(goal.dueDate);
    if (!goalsByMonth[monthIdx]) goalsByMonth[monthIdx] = { completed: [], active: [], overdue: [] };
    goalsByMonth[monthIdx].active.push(goal);
  });

  completedGoals.forEach(goal => {
    const monthIdx = getMonthIndex(goal.dueDate);
    if (!goalsByMonth[monthIdx]) goalsByMonth[monthIdx] = { completed: [], active: [], overdue: [] };
    goalsByMonth[monthIdx].completed.push(goal);
  });

  Object.values(goalsByMonth).forEach(month => {
    month.completed.sort((a, b) => {
      const aDate = getCompletedAtDate(a.completedAt);
      const bDate = getCompletedAtDate(b.completedAt);
      if (!aDate) return 1;
      if (!bDate) return -1;
      return aDate - bDate;
    });
  });

  if (overdueGoals.length > 0) {
    if (!goalsByMonth[currentMonth]) goalsByMonth[currentMonth] = { completed: [], active: [], overdue: [] };
    goalsByMonth[currentMonth].overdue = overdueGoals;
  }

  const monthsWithGoals = Object.keys(goalsByMonth)
    .map(Number)
    .sort((a, b) => a - b);

  const GoalCard = ({ goal, colors, isOverdue: isOverdueGoal, originalMonth, isLeft }) => {
    const cardClasses = goal.completed
      ? 'bg-green-500/10 border-green-500/30'
      : isOverdueGoal
        ? 'bg-red-500/10 border-red-500/30'
        : `${colors.bg} ${colors.border}`;

    const dotClasses = goal.completed
      ? 'bg-green-500'
      : isOverdueGoal
        ? 'bg-red-500'
        : colors.dot;

    const textClasses = goal.completed
      ? 'text-green-400 line-through'
      : isOverdueGoal
        ? 'text-red-400'
        : 'text-text-primary';

    return (
      <div className="relative flex items-center">
        {isLeft && (
          <>
            <div className={`absolute right-0 top-1/2 w-6 border-t-2 border-dashed ${goal.completed ? 'border-green-500/50' : isOverdueGoal ? 'border-red-500/50' : colors.border}`} />
            <div className={`absolute -right-[30px] top-1/2 -translate-y-1/2 w-3 h-3 rounded-full ${dotClasses} shadow-md z-10`} />
          </>
        )}

        {!isLeft && (
          <>
            <div className={`absolute left-0 top-1/2 w-6 border-t-2 border-dashed ${goal.completed ? 'border-green-500/50' : isOverdueGoal ? 'border-red-500/50' : colors.border}`} />
            <div className={`absolute -left-[30px] top-1/2 -translate-y-1/2 w-3 h-3 rounded-full ${dotClasses} shadow-md z-10`} />
          </>
        )}

        <div className={`flex-1 p-3 rounded-xl border-2 transition-all hover:scale-[1.02] hover:shadow-lg ${cardClasses}`}>
          <div className="flex items-start gap-2">
            <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${dotClasses}`}>
              {goal.completed ? (
                <CheckCircle2 size={12} className="text-white" />
              ) : (
                <Circle size={10} className="text-white" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <span className={`text-xs font-semibold block ${textClasses}`}>
                {goal.title}
              </span>
              {isOverdueGoal && originalMonth !== undefined && (
                <span className="text-[10px] text-red-400 font-medium block mt-0.5">
                  Missed from {monthNames[originalMonth]}
                </span>
              )}
              {goal.description && (
                <p className="text-[10px] text-text-tertiary mt-1">{goal.description}</p>
              )}
              {getGoalOwnerName && (
                <span className="text-[10px] text-text-muted mt-1 block">{getGoalOwnerName(goal)}</span>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-1">
      {/* ========== MOBILE: Simple left-aligned timeline ========== */}
      <div className="md:hidden relative pl-6">
        <div className="absolute left-2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-blue-500 via-emerald-500 via-amber-500 to-purple-500 rounded-full opacity-40" />

        {monthsWithGoals.map((monthIdx) => {
          const monthData = goalsByMonth[monthIdx];
          const firstGoal = monthData.completed[0] || monthData.active[0] || monthData.overdue[0];
          const quarter = getQuarter(firstGoal?.dueDate);
          const colors = quarterColors[quarter] || quarterColors.Q1;

          const allGoals = [...monthData.completed, ...monthData.overdue, ...monthData.active];

          return (
            <div key={monthIdx} className="relative mb-6">
              {/* Month label */}
              <div className="flex items-center gap-2 mb-3">
                <div className={`absolute left-0 w-4 h-4 rounded-full ${colors.dot} shadow-md`} />
                <span className={`text-xs font-bold ${colors.text}`}>{monthNames[monthIdx]}</span>
              </div>

              {/* Goals */}
              <div className="space-y-2">
                {allGoals.map((goal) => {
                  const isOverdueGoal = !goal.completed && isGoalOverdue(goal.dueDate);
                  const cardClasses = goal.completed
                    ? 'bg-green-500/10 border-green-500/30'
                    : isOverdueGoal
                      ? 'bg-red-500/10 border-red-500/30'
                      : `${colors.bg} ${colors.border}`;
                  const textClasses = goal.completed
                    ? 'text-green-400 line-through'
                    : isOverdueGoal
                      ? 'text-red-400'
                      : 'text-text-primary';

                  return (
                    <div key={goal.id} className={`p-3 rounded-xl border ${cardClasses}`}>
                      <div className="flex items-start gap-2">
                        <div className={`w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 ${goal.completed ? 'bg-green-500' : isOverdueGoal ? 'bg-red-500' : colors.dot}`}>
                          {goal.completed ? (
                            <CheckCircle2 size={10} className="text-white" />
                          ) : (
                            <Circle size={8} className="text-white" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <span className={`text-xs font-semibold block ${textClasses}`}>{goal.title}</span>
                          {isOverdueGoal && (
                            <span className="text-[10px] text-red-400 font-medium">Overdue</span>
                          )}
                          {getGoalOwnerName && (
                            <span className="text-[10px] text-text-muted block">{getGoalOwnerName(goal)}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* ========== DESKTOP: Alternating left/right timeline ========== */}
      <div className="hidden md:block relative">
        <div className="absolute left-1/2 -translate-x-1/2 top-0 bottom-0 w-1 bg-gradient-to-b from-blue-500 via-emerald-500 via-amber-500 to-purple-500 rounded-full opacity-40" />

        {monthsWithGoals.map((monthIdx) => {
          const monthData = goalsByMonth[monthIdx];
          const isLeft = monthIdx % 2 === 0;

          const firstGoal = monthData.completed[0] || monthData.active[0] || monthData.overdue[0];
          const quarter = getQuarter(firstGoal?.dueDate);
          const colors = quarterColors[quarter] || quarterColors.Q1;

          return (
            <div key={monthIdx} className="relative mb-8">
              <div className="flex items-center justify-center mb-4">
                <div className={`px-4 py-1.5 rounded-full ${colors.bg} ${colors.border} border shadow-lg z-10`}>
                  <span className={`text-xs font-bold ${colors.text}`}>{monthNames[monthIdx]}</span>
                </div>
              </div>

              <div className="relative flex">
                <div className={`w-[calc(50%-24px)] ${isLeft ? 'pr-0' : ''}`}>
                  {isLeft && (
                    <div className="space-y-4">
                      {monthData.completed.map((goal) => (
                        <GoalCard key={goal.id} goal={goal} colors={colors} isLeft={true} />
                      ))}
                      {monthData.overdue.map((goal) => (
                        <GoalCard
                          key={goal.id}
                          goal={goal}
                          colors={colors}
                          isOverdue={true}
                          originalMonth={getMonthIndex(goal.dueDate)}
                          isLeft={true}
                        />
                      ))}
                      {monthData.active.map((goal) => (
                        <GoalCard key={goal.id} goal={goal} colors={colors} isLeft={true} />
                      ))}
                    </div>
                  )}
                </div>

                <div className="w-12 flex-shrink-0" />

                <div className={`w-[calc(50%-24px)] ${!isLeft ? 'pl-0' : ''}`}>
                  {!isLeft && (
                    <div className="space-y-4">
                      {monthData.completed.map((goal) => (
                        <GoalCard key={goal.id} goal={goal} colors={colors} isLeft={false} />
                      ))}
                      {monthData.overdue.map((goal) => (
                        <GoalCard
                          key={goal.id}
                          goal={goal}
                          colors={colors}
                          isOverdue={true}
                          originalMonth={getMonthIndex(goal.dueDate)}
                          isLeft={false}
                        />
                      ))}
                      {monthData.active.map((goal) => (
                        <GoalCard key={goal.id} goal={goal} colors={colors} isLeft={false} />
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}

        {goals.length === 0 && (
          <div className="text-center py-12 text-text-muted text-sm">
            No goals to display. Add goals using the List view!
          </div>
        )}

        {goals.length > 0 && (
          <div className="flex flex-wrap items-center justify-center gap-4 pt-4 border-t border-border mt-4">
            {['Q1', 'Q2', 'Q3', 'Q4'].map(q => (
              <div key={q} className="flex items-center gap-1.5">
                <div className={`w-3 h-3 rounded-full ${quarterColors[q].dot}`} />
                <span className="text-[10px] text-text-tertiary">{q}</span>
              </div>
            ))}
            <div className="flex items-center gap-1.5 ml-2">
              <div className="w-3 h-3 rounded-full bg-green-500" />
              <span className="text-[10px] text-text-tertiary">Completed</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded-full bg-red-500" />
              <span className="text-[10px] text-text-tertiary">Overdue</span>
            </div>
          </div>
        )}
      </div>

      {/* Mobile empty state and legend */}
      <div className="md:hidden">
        {goals.length === 0 && (
          <div className="text-center py-8 text-text-muted text-sm">
            No goals to display. Add goals using the List view!
          </div>
        )}

        {goals.length > 0 && (
          <div className="flex flex-wrap items-center justify-center gap-3 pt-4 border-t border-border mt-4">
            <div className="flex items-center gap-1.5">
              <div className="w-2.5 h-2.5 rounded-full bg-green-500" />
              <span className="text-[10px] text-text-tertiary">Done</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-2.5 h-2.5 rounded-full bg-red-500" />
              <span className="text-[10px] text-text-tertiary">Overdue</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function DocumentsPage({ onAddGoal }) {
  const { isAdmin, user } = useAuth();
  const { documents, archivedDocuments, yearlyDocs, quarterlyDocs, monthlyDocs, otherDocs, addDocument, updateDocument, deleteDocument, archiveDocument, unarchiveDocument, loading: docsLoading } = useDocuments();
  const { goals, upcomingGoals, overdueGoals, completedGoals, addGoal, updateGoal, deleteGoal, toggleCompleted, isOverdue, canEdit: canEditGoal, getGoalOwnerName, isMyGoal, loading: goalsLoading } = useGoals();
  const { projects, addProject, updateProject, deleteProject, getProjectsByGoal, loading: projectsLoading } = useProjects();
  const { tasks, addTask, completeTask, uncompleteTask, getTasksByProject, getTasksByGoal } = useTasks();
  const { timeBlocks } = useTimeBlocks();

  // Get current user name
  const currentUserName = user?.displayName?.split(' ')[0] || 'Jason';

  const [activeView, setActiveView] = useState('list');
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [showGoalModal, setShowGoalModal] = useState(false);
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [editingDocument, setEditingDocument] = useState(null);
  const [editingGoal, setEditingGoal] = useState(null);
  const [editingProject, setEditingProject] = useState(null);
  const [prefilledProjectId, setPrefilledProjectId] = useState(null);
  const [prefilledGoalId, setPrefilledGoalId] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState({ show: false, type: null, item: null });
  const [expandedGoals, setExpandedGoals] = useState(new Set());
  const [selectedGoal, setSelectedGoal] = useState(null);

  // Toggle goal expansion
  const toggleGoalExpanded = (goalId) => {
    setExpandedGoals(prev => {
      const next = new Set(prev);
      if (next.has(goalId)) {
        next.delete(goalId);
      } else {
        next.add(goalId);
      }
      return next;
    });
  };

  // Get projects without a goal (misc projects)
  const miscProjects = projects.filter(p => !p.goalId && p.status === 'active');

  const handleAddDocument = () => {
    setEditingDocument(null);
    setShowDocumentModal(true);
  };

  const handleEditDocument = (doc) => {
    setEditingDocument(doc);
    setShowDocumentModal(true);
  };

  const handleSaveDocument = async (data) => {
    if (editingDocument) {
      await updateDocument(editingDocument.id, data);
    } else {
      await addDocument(data);
    }
  };

  const handleAddGoal = () => {
    setEditingGoal(null);
    setShowGoalModal(true);
  };

  // Expose addGoal handler to parent (for Header add button)
  if (onAddGoal) {
    onAddGoal.current = handleAddGoal;
  }

  const handleEditGoal = (goal) => {
    setEditingGoal(goal);
    setShowGoalModal(true);
  };

  const handleSaveGoal = async (data) => {
    if (editingGoal) {
      await updateGoal(editingGoal.id, data);
    } else {
      await addGoal(data);
    }
  };

  const handleAddProject = (goalId = null) => {
    setEditingProject(null);
    setPrefilledGoalId(goalId);
    setShowProjectModal(true);
  };

  const handleEditProject = (project) => {
    setEditingProject(project);
    setPrefilledGoalId(null);
    setShowProjectModal(true);
  };

  const handleSaveProject = async (data) => {
    if (editingProject) {
      await updateProject(editingProject.id, data);
    } else {
      await addProject(data);
    }
    setPrefilledGoalId(null);
  };

  // Task handlers
  const handleAddTaskToProject = (projectId) => {
    setPrefilledProjectId(projectId);
    setPrefilledGoalId(null);
    setShowTaskModal(true);
  };

  const handleAddTaskToGoal = (goalId) => {
    setPrefilledGoalId(goalId);
    setPrefilledProjectId(null);
    setShowTaskModal(true);
  };

  const handleSaveTask = async (taskData) => {
    await addTask({
      ...taskData,
      projectId: prefilledProjectId || taskData.projectId,
      goalId: prefilledGoalId || taskData.goalId,
      owner: currentUserName
    });
    setPrefilledProjectId(null);
    setPrefilledGoalId(null);
  };

  const handleDeleteConfirm = () => {
    if (deleteConfirm.type === 'document') {
      deleteDocument(deleteConfirm.item.id);
    } else if (deleteConfirm.type === 'goal') {
      deleteGoal(deleteConfirm.item.id);
    } else if (deleteConfirm.type === 'project') {
      deleteProject(deleteConfirm.item.id);
    }
    setDeleteConfirm({ show: false, type: null, item: null });
  };

  // Get task count for a project
  const getProjectTaskCount = (projectId) => {
    return tasks.filter(t => t.projectId === projectId && t.status !== TASK_STATUSES.DONE).length;
  };

  const loading = docsLoading || goalsLoading || projectsLoading;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-text-tertiary">Loading...</div>
      </div>
    );
  }

  const DocumentItem = ({ doc, isArchived = false }) => (
    <div className={`flex items-center gap-2 p-2.5 bg-surface-card rounded-lg border border-border mb-1.5 transition-all ${isArchived ? 'opacity-70' : ''}`}>
      <FileText size={14} className="text-text-tertiary" />
      <div className="flex-1 min-w-0">
        <a
          href={doc.url}
          target="_blank"
          rel="noopener noreferrer"
          className="font-medium text-text-primary text-xs hover:text-accent transition-colors"
        >
          {doc.name}
        </a>
        <div className="flex items-center gap-1.5 mt-0.5">
          <span className="text-[10px] bg-surface-hover px-1.5 py-0.5 rounded capitalize text-text-tertiary">{doc.type}</span>
          <span className="text-[10px] text-text-tertiary">{doc.owner}</span>
          {doc.weekOf && (
            <>
              <span className="text-[10px] text-text-muted">•</span>
              <span className="text-[10px] text-text-tertiary">Week of {formatWeekOf(doc.weekOf)}</span>
            </>
          )}
        </div>
      </div>
      <div className="flex gap-0.5">
        <a
          href={doc.url}
          target="_blank"
          rel="noopener noreferrer"
          className="p-1.5 rounded-md bg-surface-hover text-text-secondary flex items-center justify-center no-underline transition-all hover:bg-accent-muted hover:text-accent"
        >
          <ExternalLink size={12} />
        </a>
        {isAdmin && (
          <>
            {isArchived ? (
              <button
                onClick={() => unarchiveDocument(doc.id)}
                className="p-1.5 rounded-md bg-transparent border-none text-text-tertiary cursor-pointer hover:bg-success-light hover:text-success transition-all"
                title="Unarchive"
              >
                <ArchiveRestore size={12} />
              </button>
            ) : (
              <button
                onClick={() => archiveDocument(doc.id)}
                className="p-1.5 rounded-md bg-transparent border-none text-text-tertiary cursor-pointer hover:bg-accent-muted hover:text-accent transition-all"
                title="Archive"
              >
                <Archive size={12} />
              </button>
            )}
            <button
              onClick={() => handleEditDocument(doc)}
              className="p-1.5 rounded-md bg-transparent border-none text-text-tertiary cursor-pointer hover:bg-surface-hover hover:text-text-primary transition-all"
            >
              <Edit2 size={12} />
            </button>
            <button
              onClick={() => setDeleteConfirm({ show: true, type: 'document', item: doc })}
              className="p-1.5 rounded-md bg-transparent border-none text-text-tertiary cursor-pointer hover:bg-red-500/10 hover:text-red-400 transition-all"
            >
              <Trash2 size={12} />
            </button>
          </>
        )}
      </div>
    </div>
  );

  // Project item component for display under goals or in misc
  const ProjectItem = ({ project }) => {
    const taskCount = getProjectTaskCount(project.id);
    return (
      <div className="flex items-center gap-2 p-2 bg-surface-elevated rounded-lg border border-border/50 ml-4 mb-1">
        <Folder size={12} className="text-purple-400 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-[11px] text-text-primary font-medium truncate">{project.name}</p>
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] text-text-muted">{taskCount} task{taskCount !== 1 ? 's' : ''}</span>
            {project.dueDate && (
              <>
                <span className="text-[10px] text-text-muted">•</span>
                <span className="text-[10px] text-text-muted">Due {formatDate(project.dueDate)}</span>
              </>
            )}
          </div>
        </div>
        {isAdmin && (
          <div className="flex gap-0.5">
            <button
              onClick={() => handleAddTaskToProject(project.id)}
              className="p-1 rounded-md bg-transparent border-none text-accent cursor-pointer hover:bg-accent-muted transition-all"
              title="Add task"
            >
              <Plus size={10} />
            </button>
            <button
              onClick={() => handleEditProject(project)}
              className="p-1 rounded-md bg-transparent border-none text-text-tertiary cursor-pointer hover:bg-surface-hover hover:text-text-primary transition-all"
            >
              <Edit2 size={10} />
            </button>
            <button
              onClick={() => setDeleteConfirm({ show: true, type: 'project', item: project })}
              className="p-1 rounded-md bg-transparent border-none text-text-tertiary cursor-pointer hover:bg-red-500/10 hover:text-red-400 transition-all"
            >
              <Trash2 size={10} />
            </button>
          </div>
        )}
      </div>
    );
  };

  const GoalItem = ({ goal, showCheckbox = true }) => {
    const goalProjects = getProjectsByGoal(goal.id).filter(p => p.status === 'active');
    const directTasks = getTasksByGoal(goal.id); // Tasks linked directly to goal (no project)
    const isExpanded = expandedGoals.has(goal.id);
    const hasContent = goalProjects.length > 0 || directTasks.length > 0;

    // Task item component for inline display
    const TaskInlineItem = ({ task }) => {
      const isComplete = !!task.completedAt;
      return (
        <div className={`flex items-center gap-2 py-1.5 px-2 rounded-md ${isComplete ? 'opacity-50' : ''}`}>
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (isComplete) {
                uncompleteTask(task.id);
              } else {
                completeTask(task.id);
              }
            }}
            className="flex-shrink-0 cursor-pointer bg-transparent border-none p-0"
          >
            {isComplete ? (
              <CheckCircle2 size={14} className="text-green-500" />
            ) : (
              <Circle size={14} className="text-text-muted hover:text-accent transition-colors" />
            )}
          </button>
          <span className={`text-[11px] ${isComplete ? 'line-through text-text-muted' : 'text-text-secondary'}`}>
            {task.title}
          </span>
        </div>
      );
    };

    // Expanded project with tasks
    const ExpandedProjectItem = ({ project }) => {
      const projectTasks = getTasksByProject(project.id);
      const activeTasks = projectTasks.filter(t => t.status !== TASK_STATUSES.DONE);
      const completedTasks = projectTasks.filter(t => t.status === TASK_STATUSES.DONE);

      return (
        <div className="ml-4 mb-2 p-2.5 bg-surface-elevated rounded-lg border border-border/50">
          <div className="flex items-center gap-2 mb-1">
            <Folder size={12} className="text-purple-400 flex-shrink-0" />
            <span className="text-[11px] text-text-primary font-medium flex-1">{project.name}</span>
            {isAdmin && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleAddTaskToProject(project.id);
                }}
                className="p-1 rounded-md bg-transparent border-none text-accent cursor-pointer hover:bg-accent-muted transition-all"
                title="Add task"
              >
                <Plus size={10} />
              </button>
            )}
          </div>
          {projectTasks.length === 0 ? (
            <p className="text-[10px] text-text-muted ml-5 italic">No tasks yet</p>
          ) : (
            <div className="ml-3">
              {activeTasks.map(task => (
                <TaskInlineItem key={task.id} task={task} />
              ))}
              {completedTasks.length > 0 && activeTasks.length > 0 && (
                <div className="border-t border-border/30 my-1" />
              )}
              {completedTasks.slice(0, 3).map(task => (
                <TaskInlineItem key={task.id} task={task} />
              ))}
              {completedTasks.length > 3 && (
                <p className="text-[10px] text-text-muted ml-5">+{completedTasks.length - 3} more completed</p>
              )}
            </div>
          )}
        </div>
      );
    };

    return (
      <div className={`bg-surface-card rounded-lg border mb-1.5 transition-all ${isOverdue(goal.dueDate) && !goal.completed ? 'border-red-500/30' : 'border-border'} ${goal.completed ? 'opacity-60' : ''} ${selectedGoal?.id === goal.id ? 'ring-2 ring-accent' : ''}`}>
        {/* Goal Header - Clickable */}
        <div
          className="p-2.5 flex items-start gap-2 cursor-pointer hover:bg-surface-hover/50"
          onClick={() => setSelectedGoal(goal)}
        >
          {/* Expand chevron */}
          {hasContent && !goal.completed ? (
            <ChevronRight
              size={14}
              className={`text-text-muted mt-0.5 flex-shrink-0 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
            />
          ) : (
            <div className="w-3.5" />
          )}

          {showCheckbox && canEditGoal(goal) && (
            <input
              type="checkbox"
              checked={goal.completed}
              onChange={(e) => {
                e.stopPropagation();
                toggleCompleted(goal.id, goal.completed);
              }}
              onClick={(e) => e.stopPropagation()}
              className="w-3.5 h-3.5 mt-0.5 cursor-pointer accent-accent"
            />
          )}
          <div className="flex-1 min-w-0">
            <p className={`text-xs text-text-primary font-medium ${goal.completed ? 'line-through' : ''}`}>
              {goal.title}
            </p>
            {goal.description && (
              <p className="text-[10px] text-text-tertiary mt-0.5">{goal.description}</p>
            )}
            <div className="flex items-center gap-1.5 mt-0.5">
              <span className="text-[10px] text-text-tertiary">{getGoalOwnerName(goal)}</span>
              <span className="text-[10px] text-text-muted">•</span>
              <span className={`text-[10px] ${isOverdue(goal.dueDate) && !goal.completed ? 'text-red-400 font-medium' : 'text-text-tertiary'}`}>
                {isOverdue(goal.dueDate) && !goal.completed ? 'OVERDUE - ' : ''}
                Due {formatDate(goal.dueDate)}
              </span>
              {goalProjects.length > 0 && (
                <>
                  <span className="text-[10px] text-text-muted">•</span>
                  <span className="text-[10px] text-purple-400">{goalProjects.length} project{goalProjects.length !== 1 ? 's' : ''}</span>
                </>
              )}
              {directTasks.filter(t => t.status !== TASK_STATUSES.DONE).length > 0 && (
                <>
                  <span className="text-[10px] text-text-muted">•</span>
                  <span className="text-[10px] text-accent">{directTasks.filter(t => t.status !== TASK_STATUSES.DONE).length} task{directTasks.filter(t => t.status !== TASK_STATUSES.DONE).length !== 1 ? 's' : ''}</span>
                </>
              )}
            </div>
          </div>
          {/* Add task/project buttons visible for all goals, edit/delete only for own goals */}
          <div className="flex gap-0.5" onClick={(e) => e.stopPropagation()}>
            {isAdmin && (
              <>
                <button
                  onClick={() => handleAddTaskToGoal(goal.id)}
                  className="p-1.5 rounded-md bg-transparent border-none text-accent cursor-pointer hover:bg-accent-muted transition-all"
                  title="Add task to goal"
                >
                  <Plus size={12} />
                </button>
                <button
                  onClick={() => handleAddProject(goal.id)}
                  className="p-1.5 rounded-md bg-transparent border-none text-purple-400 cursor-pointer hover:bg-purple-500/10 transition-all"
                  title="Add project"
                >
                  <Folder size={12} />
                </button>
              </>
            )}
            {canEditGoal(goal) && (
              <>
                <button
                  onClick={() => handleEditGoal(goal)}
                  className="p-1.5 rounded-md bg-transparent border-none text-text-tertiary cursor-pointer hover:bg-surface-hover hover:text-text-primary transition-all"
                >
                  <Edit2 size={12} />
                </button>
                <button
                  onClick={() => setDeleteConfirm({ show: true, type: 'goal', item: goal })}
                  className="p-1.5 rounded-md bg-transparent border-none text-text-tertiary cursor-pointer hover:bg-red-500/10 hover:text-red-400 transition-all"
                >
                  <Trash2 size={12} />
                </button>
              </>
            )}
          </div>
        </div>

        {/* Expanded Content - Projects with Tasks and Direct Tasks */}
        {isExpanded && !goal.completed && hasContent && (
          <div className="pb-2 px-2">
            {/* Direct tasks (not in a project) */}
            {directTasks.length > 0 && (
              <div className="ml-4 mb-2 p-2.5 bg-surface-elevated rounded-lg border border-border/50">
                <div className="flex items-center gap-2 mb-1">
                  <Target size={12} className="text-monthly flex-shrink-0" />
                  <span className="text-[11px] text-text-primary font-medium flex-1">Direct Tasks</span>
                </div>
                <div className="ml-3">
                  {directTasks.filter(t => t.status !== TASK_STATUSES.DONE).map(task => (
                    <TaskInlineItem key={task.id} task={task} />
                  ))}
                  {directTasks.filter(t => t.status === TASK_STATUSES.DONE).length > 0 &&
                   directTasks.filter(t => t.status !== TASK_STATUSES.DONE).length > 0 && (
                    <div className="border-t border-border/30 my-1" />
                  )}
                  {directTasks.filter(t => t.status === TASK_STATUSES.DONE).slice(0, 3).map(task => (
                    <TaskInlineItem key={task.id} task={task} />
                  ))}
                  {directTasks.filter(t => t.status === TASK_STATUSES.DONE).length > 3 && (
                    <p className="text-[10px] text-text-muted ml-5">
                      +{directTasks.filter(t => t.status === TASK_STATUSES.DONE).length - 3} more completed
                    </p>
                  )}
                </div>
              </div>
            )}
            {/* Projects with tasks */}
            {goalProjects.map(project => (
              <ExpandedProjectItem key={project.id} project={project} />
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <FeatureOnboarding feature="goals">
    <div>
      {/* View Toggle */}
      <div className="flex items-center gap-2 mb-4">
        <button
          onClick={() => setActiveView('list')}
          className={`px-3 py-1.5 rounded-lg text-xs font-medium cursor-pointer flex items-center gap-1.5 border transition-all ${
            activeView === 'list'
              ? 'bg-accent text-surface-base border-accent'
              : 'bg-surface-card border-border text-text-secondary hover:border-accent/50 hover:text-text-primary'
          }`}
        >
          <List size={14} />
          List View
        </button>
        <button
          onClick={() => setActiveView('timeline')}
          className={`px-3 py-1.5 rounded-lg text-xs font-medium cursor-pointer flex items-center gap-1.5 border transition-all ${
            activeView === 'timeline'
              ? 'bg-accent text-surface-base border-accent'
              : 'bg-surface-card border-border text-text-secondary hover:border-accent/50 hover:text-text-primary'
          }`}
        >
          <Map size={14} />
          Timeline View
        </button>
      </div>

      {/* Timeline View */}
      {activeView === 'timeline' && (
        <div className="bg-surface-card rounded-xl border border-border p-4">
          <div className="mb-4">
            <h2 className="text-base font-semibold text-text-primary">Goals Timeline</h2>
            <p className="text-xs text-text-tertiary mt-1">
              Visual roadmap of your goals by month. Switch to List View to add or edit goals.
            </p>
          </div>
          <TimelineView goals={goals} getGoalOwnerName={getGoalOwnerName} />
        </div>
      )}

      {/* List View */}
      {activeView === 'list' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Documents Column */}
          <div>
            <Section
              title="KRA & Goal Documents"
              icon={FileText}
              iconColor="text-text-tertiary"
              count={documents.length}
              onAdd={isAdmin ? handleAddDocument : undefined}
            >
              {yearlyDocs.length > 0 && (
                <div className="mb-3">
                  <h4 className="text-[10px] font-medium text-text-tertiary uppercase tracking-wide mb-1.5">Yearly</h4>
                  {yearlyDocs.map((doc) => (
                    <DocumentItem key={doc.id} doc={doc} />
                  ))}
                </div>
              )}

              {quarterlyDocs.length > 0 && (
                <div className="mb-3">
                  <h4 className="text-[10px] font-medium text-text-tertiary uppercase tracking-wide mb-1.5">Quarterly</h4>
                  {quarterlyDocs.map((doc) => (
                    <DocumentItem key={doc.id} doc={doc} />
                  ))}
                </div>
              )}

              {monthlyDocs.length > 0 && (
                <div className="mb-3">
                  <h4 className="text-[10px] font-medium text-text-tertiary uppercase tracking-wide mb-1.5">Monthly</h4>
                  {monthlyDocs.map((doc) => (
                    <DocumentItem key={doc.id} doc={doc} />
                  ))}
                </div>
              )}

              {otherDocs.length > 0 && (
                <div className="mb-3">
                  <h4 className="text-[10px] font-medium text-text-tertiary uppercase tracking-wide mb-1.5">Other</h4>
                  {otherDocs.map((doc) => (
                    <DocumentItem key={doc.id} doc={doc} />
                  ))}
                </div>
              )}

              {documents.length === 0 && (
                <p className="text-text-tertiary text-sm italic">
                  No documents yet. Add your KRA & Goals documents!
                </p>
              )}
            </Section>

            {/* Archived Documents Section */}
            {archivedDocuments.length > 0 && (
              <Section
                title="Archived"
                icon={Archive}
                iconColor="text-text-tertiary"
                count={archivedDocuments.length}
                defaultOpen={false}
              >
                {archivedDocuments.map((doc) => (
                  <DocumentItem key={doc.id} doc={doc} isArchived={true} />
                ))}
              </Section>
            )}
          </div>

          {/* Goals Column */}
          <div>
            {/* Misc Projects - projects without a goal */}
            <Section
              title="Misc Projects"
              icon={Folder}
              iconColor="text-purple-400"
              count={miscProjects.length}
              onAdd={isAdmin ? handleAddProject : undefined}
            >
              {miscProjects.length > 0 && (
                miscProjects.map((project) => {
                  const taskCount = getProjectTaskCount(project.id);
                  return (
                    <div
                      key={project.id}
                      className="flex items-center gap-2 p-2.5 bg-surface-card rounded-lg border border-border mb-1.5"
                    >
                      <Folder size={14} className="text-purple-400 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-text-primary font-medium truncate">{project.name}</p>
                        {project.description && (
                          <p className="text-[10px] text-text-tertiary mt-0.5 truncate">{project.description}</p>
                        )}
                        <div className="flex items-center gap-1.5 mt-0.5">
                          <span className="text-[10px] text-text-muted">{taskCount} task{taskCount !== 1 ? 's' : ''}</span>
                          {project.dueDate && (
                            <>
                              <span className="text-[10px] text-text-muted">•</span>
                              <span className="text-[10px] text-text-muted">Due {formatDate(project.dueDate)}</span>
                            </>
                          )}
                          {project.sourceChannel && (
                            <>
                              <span className="text-[10px] text-text-muted">•</span>
                              <a
                                href={project.sourceLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-[10px] text-purple-400 hover:text-purple-300"
                              >
                                #{project.sourceChannel}
                              </a>
                            </>
                          )}
                        </div>
                      </div>
                      {isAdmin && (
                        <div className="flex gap-0.5">
                          <button
                            onClick={() => handleAddTaskToProject(project.id)}
                            className="p-1.5 rounded-md bg-transparent border-none text-accent cursor-pointer hover:bg-accent-muted transition-all"
                            title="Add task"
                          >
                            <Plus size={12} />
                          </button>
                          <button
                            onClick={() => handleEditProject(project)}
                            className="p-1.5 rounded-md bg-transparent border-none text-text-tertiary cursor-pointer hover:bg-surface-hover hover:text-text-primary transition-all"
                          >
                            <Edit2 size={12} />
                          </button>
                          <button
                            onClick={() => setDeleteConfirm({ show: true, type: 'project', item: project })}
                            className="p-1.5 rounded-md bg-transparent border-none text-text-tertiary cursor-pointer hover:bg-red-500/10 hover:text-red-400 transition-all"
                          >
                            <Trash2 size={12} />
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </Section>

            {/* Overdue Goals */}
            {overdueGoals.length > 0 && (
              <Section
                title="Overdue Goals"
                icon={AlertTriangle}
                iconColor="text-red-400"
                count={overdueGoals.length}
                defaultOpen={true}
              >
                {overdueGoals.map((goal) => (
                  <GoalItem key={goal.id} goal={goal} />
                ))}
              </Section>
            )}

            {/* Upcoming Goals */}
            <Section
              title="Upcoming Goals"
              icon={Target}
              iconColor="text-monthly"
              count={upcomingGoals.filter(g => !isOverdue(g.dueDate)).length}
              onAdd={isAdmin ? handleAddGoal : undefined}
            >
              {(() => {
                const nonOverdueGoals = upcomingGoals.filter(g => !isOverdue(g.dueDate));

                const getQuarterLocal = (dateStr) => {
                  if (!dateStr) return 'No Date';
                  const month = new Date(dateStr + 'T00:00:00').getMonth();
                  if (month <= 2) return 'Q1';
                  if (month <= 5) return 'Q2';
                  if (month <= 8) return 'Q3';
                  return 'Q4';
                };

                const getYear = (dateStr) => {
                  if (!dateStr) return '';
                  return new Date(dateStr + 'T00:00:00').getFullYear();
                };

                const grouped = {};
                nonOverdueGoals.forEach(goal => {
                  const year = getYear(goal.dueDate);
                  const quarter = getQuarterLocal(goal.dueDate);
                  const key = year ? `${quarter} ${year}` : 'No Date';
                  if (!grouped[key]) grouped[key] = [];
                  grouped[key].push(goal);
                });

                const sortedKeys = Object.keys(grouped).sort((a, b) => {
                  if (a === 'No Date') return 1;
                  if (b === 'No Date') return -1;
                  const [qA, yA] = a.split(' ');
                  const [qB, yB] = b.split(' ');
                  if (yA !== yB) return parseInt(yA) - parseInt(yB);
                  return parseInt(qA.slice(1)) - parseInt(qB.slice(1));
                });

                if (nonOverdueGoals.length === 0) {
                  return (
                    <p className="text-text-tertiary text-sm italic">
                      No upcoming goals. Add your goals and milestones!
                    </p>
                  );
                }

                return sortedKeys.map(key => (
                  <div key={key} className="mb-3">
                    <h4 className="text-[10px] font-medium text-text-tertiary uppercase tracking-wide mb-1.5">{key}</h4>
                    {grouped[key].map((goal) => (
                      <GoalItem key={goal.id} goal={goal} />
                    ))}
                  </div>
                ));
              })()}
            </Section>

            {/* Completed Goals */}
            {completedGoals.length > 0 && (
              <Section
                title="Completed Goals"
                icon={CheckCircle}
                iconColor="text-success"
                count={completedGoals.length}
                defaultOpen={false}
              >
                {completedGoals.map((goal) => (
                  <GoalItem key={goal.id} goal={goal} />
                ))}
              </Section>
            )}
          </div>
        </div>
      )}

      {/* Modals */}
      <DocumentModal
        isOpen={showDocumentModal}
        onClose={() => {
          setShowDocumentModal(false);
          setEditingDocument(null);
        }}
        onSave={handleSaveDocument}
        editItem={editingDocument}
      />

      <GoalModal
        isOpen={showGoalModal}
        onClose={() => {
          setShowGoalModal(false);
          setEditingGoal(null);
        }}
        onSave={handleSaveGoal}
        editItem={editingGoal}
        documents={documents}
      />

      <ProjectModal
        isOpen={showProjectModal}
        onClose={() => {
          setShowProjectModal(false);
          setEditingProject(null);
          setPrefilledGoalId(null);
        }}
        onSave={handleSaveProject}
        editItem={editingProject || (prefilledGoalId ? { goalId: prefilledGoalId } : null)}
        goals={goals}
      />

      <TaskModal
        isOpen={showTaskModal}
        onClose={() => {
          setShowTaskModal(false);
          setPrefilledProjectId(null);
          setPrefilledGoalId(null);
        }}
        onSave={handleSaveTask}
        editItem={
          prefilledProjectId
            ? { projectId: prefilledProjectId, status: 'next' }
            : prefilledGoalId
              ? { goalId: prefilledGoalId, status: 'next' }
              : null
        }
        currentUserName={currentUserName}
        timeBlocks={timeBlocks}
        projects={projects}
        goals={goals}
        getTasksForBlock={(blockId) => tasks.filter(t => t.gcalTimeBlockId === blockId)}
      />

      <ConfirmModal
        isOpen={deleteConfirm.show}
        onClose={() => setDeleteConfirm({ show: false, type: null, item: null })}
        onConfirm={handleDeleteConfirm}
        title={`Delete ${deleteConfirm.type === 'document' ? 'Document' : deleteConfirm.type === 'project' ? 'Project' : 'Goal'}`}
        message={`Are you sure you want to delete "${deleteConfirm.item?.name || deleteConfirm.item?.title}"? This action cannot be undone.`}
      />

      {/* Goal Detail Panel */}
      {selectedGoal && (
        <GoalDetailPanel
          goal={selectedGoal}
          onClose={() => setSelectedGoal(null)}
          projects={projects}
          tasks={tasks}
          onUpdateGoal={updateGoal}
          onAddProject={(goalId) => {
            setPrefilledGoalId(goalId);
            setShowProjectModal(true);
          }}
          onAddTask={(projectId, goalId) => {
            setPrefilledProjectId(projectId);
            setPrefilledGoalId(goalId);
            setShowTaskModal(true);
          }}
          onCompleteTask={completeTask}
          onUncompleteTask={uncompleteTask}
          onEditGoal={(goal) => {
            setEditingGoal(goal);
            setShowGoalModal(true);
          }}
          onDeleteGoal={(goal) => {
            setDeleteConfirm({ show: true, type: 'goal', item: goal });
          }}
          isAdmin={isAdmin}
        />
      )}
    </div>
    </FeatureOnboarding>
  );
}
